// Interface for all game settings
export interface GameSettings {
    audio: {
        soundEnabled: boolean;
        soundVolume: number;
        musicEnabled: boolean;
        musicVolume: number;
    };
    cookies: {
        allowStorage: boolean;
    };
    // We can add more settings categories here later
}

// Default settings
const DEFAULT_SETTINGS: GameSettings = {
    audio: {
        soundEnabled: true,
        soundVolume: 1.0,
        musicEnabled: true,
        musicVolume: 1.0
    },
    cookies: {
        allowStorage: true
    }
};

// Settings storage key
const SETTINGS_KEY = 'echocrypt_settings';

// Class to manage game settings
export class SettingsManager {
    private static instance: SettingsManager;
    private settings: GameSettings;
    private consentGiven: boolean = false;

    private constructor() {
        // Start with default settings
        this.settings = { ...DEFAULT_SETTINGS };
        
        // Try to load settings from localStorage if available
        this.loadSettings();
    }

    public static getInstance(): SettingsManager {
        if (!SettingsManager.instance) {
            SettingsManager.instance = new SettingsManager();
        }
        return SettingsManager.instance;
    }

    // Load settings from localStorage
    private loadSettings(): void {
        try {
            const savedSettings = localStorage.getItem(SETTINGS_KEY);
            if (savedSettings) {
                const parsedSettings = JSON.parse(savedSettings) as Partial<GameSettings>;
                // Merge saved settings with defaults to ensure we have all required fields
                this.settings = this.mergeWithDefaults(parsedSettings);
                this.consentGiven = true;
            }
        } catch (error) {
            console.warn('Failed to load settings from localStorage:', error);
            // If there's an error, use default settings
            this.settings = { ...DEFAULT_SETTINGS };
        }
    }

    // Save settings to localStorage
    public saveSettings(): void {
        // Only save if storage is allowed
        if (!this.settings.cookies.allowStorage) {
            return;
        }
        
        try {
            localStorage.setItem(SETTINGS_KEY, JSON.stringify(this.settings));
        } catch (error) {
            console.warn('Failed to save settings to localStorage:', error);
        }
    }

    // Merge partial settings with defaults
    private mergeWithDefaults(partialSettings: Partial<GameSettings>): GameSettings {
        return {
            audio: {
                ...DEFAULT_SETTINGS.audio,
                ...(partialSettings.audio || {})
            },
            cookies: {
                ...DEFAULT_SETTINGS.cookies,
                ...(partialSettings.cookies || {})
            }
        };
    }

    // Get all settings
    public getSettings(): GameSettings {
        return { ...this.settings };
    }

    // Update audio settings
    public updateAudioSettings(
        soundEnabled?: boolean,
        soundVolume?: number,
        musicEnabled?: boolean,
        musicVolume?: number
    ): void {
        if (soundEnabled !== undefined) {
            this.settings.audio.soundEnabled = soundEnabled;
        }
        if (soundVolume !== undefined) {
            this.settings.audio.soundVolume = soundVolume;
        }
        if (musicEnabled !== undefined) {
            this.settings.audio.musicEnabled = musicEnabled;
        }
        if (musicVolume !== undefined) {
            this.settings.audio.musicVolume = musicVolume;
        }

        this.saveSettings();
    }

    // Set cookie consent
    public setCookieConsent(consent: boolean): void {
        this.settings.cookies.allowStorage = consent;
        this.consentGiven = true;
        
        if (consent) {
            // If consent given, save current settings
            this.saveSettings();
        } else {
            // If consent revoked, clear localStorage
            try {
                localStorage.removeItem(SETTINGS_KEY);
            } catch (error) {
                console.warn('Failed to remove settings from localStorage:', error);
            }
        }
    }

    // Check if cookie consent has been explicitly given or denied
    public hasConsentBeenGiven(): boolean {
        return this.consentGiven;
    }

    // Check if storage is allowed
    public isStorageAllowed(): boolean {
        return this.settings.cookies.allowStorage;
    }
} 